/**
 * Geometry helpers for the drill-down donut. Ported from the chart-poc
 * scene/renderer split — the layout emits flat primitives in scene
 * coordinates, and hit-testing runs against those primitives rather than
 * DOM nodes, so hover/click behaviour is independent of how the arcs are
 * rasterised.
 */

export const TAU = Math.PI * 2;

/** Chart angles run clockwise from 12 o'clock; screen angles are that minus 90°. */
export function polar(cx, cy, r, a) {
  return [cx + r * Math.sin(a), cy - r * Math.cos(a)];
}

/**
 * Which segment is under a point, in scene coordinates. The angular slack
 * lets a pointer crossing the gap between two segments stay on one of them
 * instead of flickering.
 */
export function hitTest(scene, x, y) {
  const dx = x - scene.cx;
  const dy = y - scene.cy;
  const r = Math.hypot(dx, dy);
  let a = Math.atan2(dx, -dy);
  if (a < 0) a += TAU;
  const slack = 0.015;

  // Back to front: the last arc drawn is the one on top, so it wins the hit.
  for (let i = scene.arcs.length - 1; i >= 0; i--) {
    const arc = scene.arcs[i];
    if (r < arc.r0 || r > arc.r1) continue;
    const a0 = arc.a0 - slack;
    const a1 = arc.a1 + slack;
    // A rotated start angle can push spans either side of the 0..TAU window.
    if (
      (a >= a0 && a <= a1) ||
      (a - TAU >= a0 && a - TAU <= a1) ||
      (a + TAU >= a0 && a + TAU <= a1)
    ) {
      return arc;
    }
  }
  return null;
}

let measureCtx = null;
const measureCache = new Map();

/**
 * Text width including tracking, measured with the same font the renderer
 * draws with so leader-line arm lengths match the visible text. Cached
 * because the scene is rebuilt every frame while a hover animates.
 */
export function measureText(text, size, weight, tracking) {
  const key = `${weight}|${size}|${tracking}|${text}`;
  const cached = measureCache.get(key);
  if (cached !== undefined) return cached;

  if (!measureCtx) measureCtx = document.createElement("canvas").getContext("2d");
  if (!measureCtx) return text.length * size * 0.62;
  measureCtx.font = `${weight} ${size}px ${FONT_STACK}`;
  const width = measureCtx.measureText(text).width + tracking * text.length;
  measureCache.set(key, width);
  return width;
}

// The app's UI font (oolib v2 typo), so measured widths match rendered text.
export const FONT_STACK = "'DM Sans', 'Noto Sans Devanagari', sans-serif";
